import React, { memo } from 'react';

import type { FixedFooterProps } from './FixedFooter.types';

/**
 * FixedFooter - A sticky bottom action bar for pinned CTAs.
 *
 * Used on mobile viewports to keep primary actions visible and accessible
 * during scroll. Sits above the content scroll area with proper z-index.
 *
 * @example
 * ```tsx
 * <FixedFooter>
 *   <Button variant="primary" fullWidth>
 *     + ADD WORKOUT
 *   </Button>
 * </FixedFooter>
 * ```
 */
export const FixedFooter = memo(function FixedFooter({
  children,
  className = '',
  showBorder = true,
  safeArea = true,
  ...props
}: FixedFooterProps): React.JSX.Element {
  return (
    <div
      className={`
        fixed bottom-0 left-0 right-0
        h-16 px-4 py-2
        bg-contrast-950
        ${showBorder ? 'border-t border-stroke-default' : ''}
        ${safeArea ? 'pb-safe' : ''}
        z-40
        flex items-center justify-center
        ${className}
      `
        .replace(/\s+/g, ' ')
        .trim()}
      role="contentinfo"
      aria-label="Actions"
      {...props}
    >
      {children}
    </div>
  );
});

export default FixedFooter;
